import { useState, useEffect } from 'react';
import { getOrganisations, getOrgLicenceSummary, redeemLicence } from '../api';
import Breadcrumb from '../components/Breadcrumb';

export default function OrgLicencesPage() {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [redeemKey, setRedeemKey] = useState('');
  const [redeemMsg, setRedeemMsg] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    getOrganisations().then((data) => {
      setOrgs(data);
      if (data.length === 1) setSelectedOrgId(String(data[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedOrgId) { setSummary(null); return; }
    setLoading(true);
    setError('');
    getOrgLicenceSummary(selectedOrgId)
      .then(setSummary)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedOrgId]);

  const handleRedeem = async (e) => {
    e.preventDefault();
    setError('');
    setRedeemMsg('');
    setRedeeming(true);
    try {
      await redeemLicence({ licenceKey: redeemKey.trim(), organisationId: Number(selectedOrgId) });
      setRedeemKey('');
      setRedeemMsg('Licence redeemed successfully!');
      // Reload summary
      const updated = await getOrgLicenceSummary(selectedOrgId);
      setSummary(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="page-container">
      <div className="form-card">
        <Breadcrumb items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Licences' },
        ]} />
        <h2>Organisation Licences</h2>
        {error && <div className="alert alert-error">{error}</div>}
        {redeemMsg && <div className="alert alert-success">{redeemMsg}</div>}

        <div className="form-group">
          <label>Organisation</label>
          <select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}>
            <option value="">Select organisation...</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>

        {loading && <p>Loading...</p>}

        {summary && (
          <>
            <div style={{ display: 'flex', gap: '1.5rem', margin: '1rem 0', flexWrap: 'wrap' }}>
              <div className="form-card" style={{ flex: 1, minWidth: '120px', textAlign: 'center', padding: '1rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{summary.totalCredits}</div>
                <div>Total Credits</div>
              </div>
              <div className="form-card" style={{ flex: 1, minWidth: '120px', textAlign: 'center', padding: '1rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{summary.creditsUsed}</div>
                <div>Used</div>
              </div>
              <div className="form-card" style={{ flex: 1, minWidth: '120px', textAlign: 'center', padding: '1rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: summary.creditsRemaining > 0 ? '#28a745' : '#dc3545' }}>
                  {summary.creditsRemaining}
                </div>
                <div>Remaining</div>
              </div>
            </div>

            <h3>Redeem Licence</h3>
            <form onSubmit={handleRedeem} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label>Licence Key (UUID)</label>
                <input
                  type="text"
                  value={redeemKey}
                  onChange={(e) => setRedeemKey(e.target.value)}
                  placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={redeeming}>
                {redeeming ? 'Redeeming...' : 'Redeem'}
              </button>
            </form>

            {summary.licences.length > 0 && (
              <>
                <h3 style={{ marginBottom: '0.75rem' }}>Licence History</h3>
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Licence Key</th>
                        <th>Usage</th>
                        <th style={{ textAlign: 'center' }}>Credits</th>
                        <th style={{ textAlign: 'center' }}>Remaining</th>
                        <th>Redeemed By</th>
                        <th>Redeemed</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.licences.map((l) => {
                        const pct = l.totalCredits > 0 ? Math.round((l.creditsUsed / l.totalCredits) * 100) : 0;
                        const barColor = pct >= 90 ? '#dc2626' : pct >= 70 ? '#f59e0b' : '#16a34a';
                        return (
                          <tr key={l.id}>
                            <td>
                              <code style={{ fontSize: '0.78em', padding: '2px 6px', background: '#f3f4f6', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                                {l.licenceKey}
                              </code>
                            </td>
                            <td style={{ minWidth: '130px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{ flex: 1, height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: '3px', transition: 'width 0.3s' }} />
                                </div>
                                <span style={{ fontSize: '0.75rem', color: '#888', whiteSpace: 'nowrap' }}>{l.creditsUsed}/{l.totalCredits}</span>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>{l.totalCredits}</td>
                            <td style={{ textAlign: 'center', fontWeight: 600, color: l.creditsRemaining === 0 ? '#dc2626' : '#16a34a' }}>
                              {l.creditsRemaining}
                            </td>
                            <td>{l.redeemedByName || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {l.redeemedAt ? new Date(l.redeemedAt).toLocaleDateString() : <span style={{ color: '#9ca3af' }}>—</span>}
                            </td>
                            <td style={{ color: l.notes ? '#333' : '#9ca3af', maxWidth: '200px' }}>{l.notes || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
